import { ApiService } from './api';
import {
  RestaurantCategoryResponse,
  CreateRestaurantCategoryRequest,
  UpdateRestaurantCategoryRequest,
  UpdateCategoryPrioritiesRequest,
  SimplifiedCategoryResponse,
} from '../types/restaurantCategories';
import { ApiResponse } from '../types/auth';

class RestaurantCategoriesService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getAllRestaurantCategories(): Promise<ApiResponse<RestaurantCategoryResponse[]>> {
    try {
      const data = await this.apiService.get<RestaurantCategoryResponse[]>('/admin/restaurant-categories');
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch restaurant categories',
      };
    }
  }

  async createRestaurantCategory(request: CreateRestaurantCategoryRequest): Promise<ApiResponse<RestaurantCategoryResponse>> {
    try {
      const data = await this.apiService.post<RestaurantCategoryResponse>('/admin/restaurant-categories', request);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create restaurant category',
      };
    }
  }

  async updateRestaurantCategory(
    categoryId: number,
    request: UpdateRestaurantCategoryRequest
  ): Promise<ApiResponse<RestaurantCategoryResponse>> {
    try {
      const data = await this.apiService.put<RestaurantCategoryResponse>(`/admin/restaurant-categories/${categoryId}`, request);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update restaurant category',
      };
    }
  }

  async updateCategoryPriorities(request: UpdateCategoryPrioritiesRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.patch<void>('/admin/restaurant-categories/priorities', request);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update category priorities',
      };
    }
  }

  async deleteRestaurantCategory(categoryId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(`/admin/restaurant-categories/${categoryId}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete restaurant category',
      };
    }
  }

  async attachCategoryToRestaurant(categoryId: number, restaurantId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(`/admin/restaurant-categories/${categoryId}/attach/${restaurantId}`, {});
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to attach category to restaurant',
      };
    }
  }

  async detachCategoryFromRestaurant(categoryId: number, restaurantId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(`/admin/restaurant-categories/${categoryId}/detach/${restaurantId}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to detach category from restaurant',
      };
    }
  }

  async getCategoriesForRestaurant(restaurantId: number): Promise<ApiResponse<SimplifiedCategoryResponse[]>> {
    try {
      const data = await this.apiService.get<SimplifiedCategoryResponse[]>(`/admin/restaurants/${restaurantId}/categories`);
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch categories for restaurant',
      };
    }
  }
}

export const restaurantCategoriesService = new RestaurantCategoriesService();
